import React from 'react'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Icon } from '@shopflo/ui'
import { DownloadSimple, User } from '@phosphor-icons/react'
import { useLocale } from 'src/lib/hooks/useLocal'
import {
  CustomerEligibilityErrorType,
  CustomerEligibilityOptions,
  CustomerEligibilityStateType
} from 'src/lib/types/common/customerConditions'
import CustomerSegments from './CustomerSegments'
import UtmParameters from './UtmParameters'
import RtoRisk from './RtoRisk'
import OrderRateLimit from './OrderRateLimit'
import SpecificCustomers from './SpecificCustomers'
import { allOptions } from 'src/@core/configs/customer-eligibilty/customer-eligibility'
import { getDefaultCustomerEligibilityState } from 'src/lib/utils/customer-eligibility/customer-eligibility'
import { useUser } from 'src/lib/contexts/UserContext'
import { useRouter } from 'next/router'
import { classNames } from 'src/lib/utils/helpers'
import SpecificPincodes from './SpecificPincodes'
import LifetimeOrderCount from './LifetimeOrderCount'

interface GenericCustomerEligibilityProps {
  state: CustomerEligibilityStateType
  onChange: (state: CustomerEligibilityStateType) => void
  allowedOptions?: CustomerEligibilityOptions[]
  customTrigger?: React.ReactNode
  errors?: CustomerEligibilityErrorType
  config?: {
    contentClassNames: string
  }
  onOpenChange?: (open: boolean) => void
  utmInfoDescription?: string
}

const GenericCustomerEligibility: React.FC<GenericCustomerEligibilityProps> = ({
  state,
  onChange,
  allowedOptions,
  customTrigger,
  errors,
  config,
  onOpenChange,
  utmInfoDescription
}) => {
  const { t } = useLocale()
  const { canAccess } = useUser()
  const router = useRouter()

  const options = allowedOptions ? allOptions.filter(opt => allowedOptions.includes(opt.value)) : allOptions

  const handleTypeChange = (newType: CustomerEligibilityOptions) => {
    const selectedOption = allOptions.find(opt => opt.value === newType)

    if (selectedOption?.permissionId && !canAccess(selectedOption.permissionId, 0)) {
      router.push('/apps-integrations')
      return
    }

    const newState = getDefaultCustomerEligibilityState(newType)
    onChange(newState)
  }

  const renderContent = () => {
    switch (state.type) {
      case CustomerEligibilityOptions.SPECIFIC_CUSTOMERS:
        return <SpecificCustomers state={state} onChange={onChange} error={errors?.uploadedFile} />
      case CustomerEligibilityOptions.LIFETIME_ORDER_COUNT:
        return (
          <LifetimeOrderCount
            state={state}
            onChange={onChange}
            errors={{ minimumOrders: errors?.minimumOrders, maximumOrders: errors?.maximumOrders }}
          />
        )
      case CustomerEligibilityOptions.CUSTOMER_SEGMENT:
        return <CustomerSegments state={state} onChange={onChange} error={errors?.segmentIds} />
      case CustomerEligibilityOptions.UTM_PARAMS:
        return (
          <UtmParameters state={state} onChange={onChange} error={errors?.utmData} description={utmInfoDescription} />
        )
      case CustomerEligibilityOptions.PINCODE_RTO_RISK:
        return <RtoRisk state={state} onChange={onChange} error={errors?.score} />
      case CustomerEligibilityOptions.CUSTOMER_RTO_RISK:
        return <RtoRisk state={state} onChange={onChange} error={errors?.score} />
      case CustomerEligibilityOptions.ORDER_RATE_LIMIT:
        return (
          <OrderRateLimit
            state={state}
            onChange={onChange}
            errors={{
              paymentMethod: errors?.paymentMethod,
              maxOrders: errors?.maxOrders,
              timePeriod: errors?.timePeriod
            }}
          />
        )
      case CustomerEligibilityOptions.SPECIFIC_PINCODES:
        return <SpecificPincodes state={state} onChange={onChange} error={errors?.uploadedFile} />
      default:
        return null
    }
  }

  return (
    <div>
      <div className='flex flex-col w-full '>
        <div className='flex flex-col'>
          <Select
            value={state.type}
            onValueChange={val => handleTypeChange(val as CustomerEligibilityOptions)}
            onOpenChange={onOpenChange}
          >
            {customTrigger ? (
              <SelectTrigger
                className='w-full border-none p-0 h-auto shadow-none focus:ring-0 group hover:bg-transparent flex items-center justify-start gap-1.5 cursor-pointer'
                showCaret={false}
              >
                {customTrigger}
              </SelectTrigger>
            ) : (
              <SelectTrigger
                id='customer-eligibility-trigger'
                className='w-full border-none p-0 h-auto shadow-none focus:ring-0 group flex items-center justify-start gap-1.5 cursor-pointer px-3 py-3 hover:bg-zinc-500/5 rounded-xl '
              >
                <Icon IconComponent={User} size='sm' className='text-zinc-400' />
                <span className='text-text-sm-regular text-zinc-500'>{t('discounts_v2.eligible_to')}</span>
                <span className='text-text-sm-semibold text-zinc-600 transition-colors group-hover:text-zinc-700 !lowercase flex items-center'>
                  {options.find(opt => opt.value === state.type)?.label}
                </span>
              </SelectTrigger>
            )}
            <SelectContent align='start' className={classNames('max-w-[326px] ')}>
              {options.map(option => (
                <SelectItem
                  key={option.value}
                  value={option.value}
                  className='w-[315px]'
                  id={`customer-eligibility-option-${option.value.toLowerCase().replace(/_/g, '-')}`}
                >
                  <div className='flex items-center gap-2 justify-between'>
                    <span>{option.label}</span>
                    {option.permissionId && !canAccess(option.permissionId, 0) && (
                      <div className='flex gap-1'>
                        <Icon IconComponent={DownloadSimple} size='xs' className='text-yellow-600' />
                        <span className='text-text-xs-medium text-yellow-600'>{t('install')}</span>
                      </div>
                    )}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className={classNames(config?.contentClassNames || '')}>{renderContent()}</div>
    </div>
  )
}

export default GenericCustomerEligibility
